import { useRef, useState } from "react";
import { toast } from "sonner";
import { api, formatApiError, openAuthorizedFile } from "@/lib/api";
import { AuthImage } from "@/components/AuthImage";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { EVIDENCE_PHASES, EVIDENCE_VISIBILITY, labelFrom } from "@/lib/constants";
import { Camera, StickyNote, FileText, Trash2, Upload } from "lucide-react";

export function EvidencesPanel({ serviceOrder, evidences, onChanged }) {
  const fileRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [form, setForm] = useState({
    evidence_type: "photo",
    phase: "during",
    visibility: "internal",
    title: "",
    description: "",
    file: null,
  });

  async function submit() {
    if (form.evidence_type !== "note" && !form.file) {
      return toast.error("Selecciona un archivo");
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("evidence_type", form.evidence_type);
      fd.append("phase", form.phase);
      fd.append("visibility", form.visibility);
      if (form.title) fd.append("title", form.title);
      if (form.description) fd.append("description", form.description);
      if (form.file) fd.append("file", form.file);
      await api.post(`/service-orders/${serviceOrder.id}/evidences`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("Evidencia agregada");
      setForm({ evidence_type: "photo", phase: "during", visibility: "internal", title: "", description: "", file: null });
      if (fileRef.current) fileRef.current.value = "";
      onChanged?.();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setUploading(false);
    }
  }

  async function remove(ev) {
    if (!window.confirm("¿Eliminar esta evidencia?")) return;
    try {
      await api.delete(`/service-orders/${serviceOrder.id}/evidences/${ev.id}`);
      toast.success("Evidencia eliminada");
      onChanged?.();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  }


  return (
    <div className="space-y-4" data-testid="panel-evidences">
      <div>
        <h3 className="text-lg tracking-tight">Evidencias</h3>
        <p className="text-xs text-muted-foreground">
          Fotos, notas y archivos ligados al folio. Se conservan aunque cambie el estatus.
        </p>
      </div>

      <Card className="p-5 border-border space-y-4" data-testid="evidence-form">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="space-y-2">
            <Label>Tipo</Label>
            <Select
              value={form.evidence_type}
              onValueChange={(v) => setForm({ ...form, evidence_type: v, file: v === "note" ? null : form.file })}
            >
              <SelectTrigger data-testid="ev-type"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="photo">Foto</SelectItem>
                <SelectItem value="note">Nota</SelectItem>
                <SelectItem value="file">Archivo</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label>Fase</Label>
            <Select value={form.phase} onValueChange={(v) => setForm({ ...form, phase: v })}>
              <SelectTrigger data-testid="ev-phase"><SelectValue /></SelectTrigger>
              <SelectContent>
                {EVIDENCE_PHASES.map((p) => <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label>Visibilidad</Label>
            <Select value={form.visibility} onValueChange={(v) => setForm({ ...form, visibility: v })}>
              <SelectTrigger data-testid="ev-visibility"><SelectValue /></SelectTrigger>
              <SelectContent>
                {EVIDENCE_VISIBILITY.map((v) => <SelectItem key={v.value} value={v.value}>{v.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label>Título</Label>
            <Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="ev-title" />
          </div>
          {form.evidence_type !== "note" && (
            <div className="space-y-2">
              <Label>Archivo</Label>
              <Input
                ref={fileRef}
                type="file"
                accept={form.evidence_type === "photo" ? "image/*" : "*"}
                onChange={(e) => setForm({ ...form, file: e.target.files?.[0] || null })}
                data-testid="ev-file"
              />
              <p className="text-[11px] text-muted-foreground">Máximo 10 MB.</p>
            </div>
          )}
        </div>
        <div className="space-y-2">
          <Label>Descripción / Nota</Label>
          <Textarea rows={2} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="ev-description" />
        </div>
        <div className="flex justify-end">
          <Button onClick={submit} disabled={uploading} className="btn-tenant" data-testid="ev-save">
            <Upload className="h-4 w-4 mr-2" />
            {uploading ? "Subiendo…" : "Agregar evidencia"}
          </Button>
        </div>
      </Card>

      {/* Photo gallery */}
      {evidences.length === 0 ? (
        <div className="text-sm text-muted-foreground border border-dashed border-border rounded-lg p-8 text-center bg-white">
          <Camera className="h-6 w-6 mx-auto mb-2 text-slate-300" />
          Aún no hay evidencias.
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {evidences.map((ev) => {
            const icon = ev.evidence_type === "photo" ? Camera : ev.evidence_type === "note" ? StickyNote : FileText;
            const IconEl = icon;
            const isImg = ev.evidence_type === "photo" && ev.file_url;
            const filePath = `/service-orders/${serviceOrder.id}/evidences/${ev.id}/file`;
            return (
              <Card key={ev.id} className="border-border overflow-hidden" data-testid={`evidence-${ev.id}`}>
                {isImg ? (
                  <button type="button" className="block w-full" onClick={() => openAuthorizedFile(filePath).catch((e) => toast.error(formatApiError(e)))} data-testid={`evidence-open-${ev.id}`}>
                    <AuthImage path={filePath} alt={ev.title || ""} className="w-full h-40 object-cover" testId={`evidence-img-${ev.id}`} />
                  </button>
                ) : ev.file_url ? (
                  <button type="button" className="h-40 w-full bg-slate-50 flex items-center justify-center" onClick={() => openAuthorizedFile(filePath).catch((e) => toast.error(formatApiError(e)))} data-testid={`evidence-open-${ev.id}`}>
                    <IconEl className="h-10 w-10 text-slate-300" />
                  </button>
                ) : (
                  <div className="h-40 bg-slate-50 flex items-center justify-center">
                    <IconEl className="h-10 w-10 text-slate-300" />
                  </div>
                )}
                <div className="p-3 space-y-1.5">
                  <div className="flex items-center justify-between gap-2">
                    <div className="text-sm font-medium truncate">{ev.title || labelFrom([{ value: "photo", label: "Foto" }, { value: "note", label: "Nota" }, { value: "file", label: "Archivo" }], ev.evidence_type)}</div>
                    <button onClick={() => remove(ev)} className="text-red-600 hover:text-red-700" data-testid={`ev-delete-${ev.id}`}>
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  </div>
                  {ev.description && <p className="text-xs text-slate-700 line-clamp-3">{ev.description}</p>}
                  <div className="flex items-center gap-1 flex-wrap pt-1">
                    <Badge variant="secondary" className="text-[10px]">{labelFrom(EVIDENCE_PHASES, ev.phase)}</Badge>
                    <Badge variant={ev.visibility === "customer_visible" ? "default" : "secondary"} className="text-[10px]">
                      {labelFrom(EVIDENCE_VISIBILITY, ev.visibility)}
                    </Badge>
                  </div>
                  <div className="text-[10px] text-muted-foreground">{ev.created_at?.slice(0, 16).replace("T", " ")}</div>
                </div>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}
